import { smmFetch } from './http'
import { createLogger } from '@/shared/logging/logger'
import type { FontCatalogEntry } from '@/shared/typography/font-catalog'

const log = createLogger('smm-fonts')

interface SmmFontRecord {
  id: string
  slug: string
  displayName: string
  fileR2Key: string | null
  googleFontsName: string | null
}

const loaded = new Set<string>() // slug

let catalogPromise: Promise<SmmFontRecord[]> | null = null

function loadCatalog(): Promise<SmmFontRecord[]> {
  catalogPromise ??= (async () => {
    const r = await smmFetch(`/api/fonts`)
    if (!r.ok) throw new Error(`/api/fonts HTTP ${r.status}`)
    const { fonts } = (await r.json()) as { fonts: SmmFontRecord[] }
    return fonts
  })()
  return catalogPromise
}

/** Resets the catalog cache. For tests. */
export function __resetFontCatalogCache(): void {
  catalogPromise = null
  loaded.clear()
}

/** Pre-load any custom (uploaded OR Google) fonts referenced by `slugs`. Best-effort:
 *  failures are logged but never thrown — freecut will fall back to system fonts.
 *
 *  After registering all FontFaces, we await `document.fonts.ready` so the
 *  canvas pipeline downstream (which can't pick up font additions
 *  retroactively for already-measured text) sees the new faces before it
 *  draws the timeline. Without this, text items may render with the fallback
 *  font even though the right face is in document.fonts. */
export async function loadCustomFonts(slugs: string[]): Promise<void> {
  if (slugs.length === 0) return
  let catalog: SmmFontRecord[]
  try {
    catalog = await loadCatalog()
  } catch (e) {
    log.warn('font catalog fetch failed', e)
    return
  }
  const bySlug = new Map(catalog.map((f) => [f.slug, f]))
  await Promise.all(
    slugs
      .filter((slug, i, arr) => arr.indexOf(slug) === i) // dedupe
      .map(async (slug) => {
        if (loaded.has(slug)) return
        const rec = bySlug.get(slug)
        if (!rec) return // unregistered — engine already warned; nothing to load
        try {
          if (rec.fileR2Key) {
            // Uploaded font — fetch bytes through the credentialed wrapper, then
            // construct the FontFace from the ArrayBuffer. The url() form would
            // do an anonymous cross-origin fetch which fails behind Cloudflare
            // Access (no cookie sent → 302 redirect → CORS reject).
            if (typeof FontFace === 'undefined') return
            const res = await smmFetch(`/api/fonts/${rec.id}/file`)
            if (!res.ok) throw new Error(`font fetch ${rec.id}: HTTP ${res.status}`)
            const bytes = await res.arrayBuffer()
            const face = new FontFace(slug, bytes)
            await face.load()
            document.fonts.add(face)
          } else if (rec.googleFontsName) {
            // Google Fonts — fetch the actual WOFF2 bytes from gstatic and
            // register a FontFace with the slug as family name. Symmetric
            // with the uploaded-font path above, so canvas resolves the slug
            // identically regardless of source.
            //
            // The previous implementation injected the Google Fonts CSS link
            // and tried to alias `font-family: <slug>; src: local(<displayName>)`,
            // but `local()` only matches OS-installed fonts — not the font
            // the browser just loaded over the network. Result: canvas asked
            // for the slug, found no FontFace, fell back to sans-serif.
            if (typeof FontFace === 'undefined') return
            const cssUrl = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(rec.googleFontsName)}:wght@400&display=swap`
            const cssRes = await fetch(cssUrl)
            if (!cssRes.ok) throw new Error(`google fonts css ${rec.googleFontsName}: HTTP ${cssRes.status}`)
            const css = await cssRes.text()
            const urlMatch = css.match(/url\((https?:\/\/[^)]+)\)/)
            const fontUrl = urlMatch?.[1]
            if (!fontUrl) throw new Error(`google fonts css ${rec.googleFontsName}: no url() in response`)
            const fontRes = await fetch(fontUrl)
            if (!fontRes.ok) throw new Error(`google fonts file ${rec.googleFontsName}: HTTP ${fontRes.status}`)
            const bytes = await fontRes.arrayBuffer()
            const face = new FontFace(slug, bytes)
            await face.load()
            document.fonts.add(face)
          }
          loaded.add(slug)
        } catch (e) {
          log.warn(`font load failed for ${slug}`, e)
        }
      }),
  )
  // Wait for the browser to finish parsing every newly-added FontFace.
  // document.fonts.ready resolves once the FontFaceSet's pending faces all
  // settle. Without this gate, the canvas timeline downstream may measure +
  // draw text before the new family is "live" and silently fall back to the
  // generic sans-serif.
  if (typeof document !== 'undefined' && document.fonts?.ready) {
    try { await document.fonts.ready } catch { /* don't block the editor on this */ }
  }
}

/** Scan a freecut project for text-item fontFamily values referencing custom slugs.
 *  Returns the unique list (system fonts like "Inter" are returned too, but
 *  loadCustomFonts will gracefully skip slugs absent from the catalog). */
export function collectFontSlugsFromProject(project: {
  timeline?: { items?: Array<{ type?: string; fontFamily?: string }> }
}): string[] {
  const items = project.timeline?.items ?? []
  const set = new Set<string>()
  for (const item of items) {
    if (item.type === 'text' && typeof item.fontFamily === 'string' && item.fontFamily.length > 0) {
      set.add(item.fontFamily)
    }
  }
  return [...set]
}

/** Fetch the SMM-managed font catalog and shape it for freecut's font picker.
 *  Slugs (not display names) are the persisted identifier — they match what the
 *  engine writes into project JSON and what `loadCustomFonts` knows how to
 *  resolve. Weights are uniform [400] because the SMM API does not yet track
 *  per-weight metadata; the picker uses this only for prefetching previews. */
export async function getSmmFontsCatalog(): Promise<readonly FontCatalogEntry[]> {
  let catalog: SmmFontRecord[]
  try {
    catalog = await loadCatalog()
  } catch (e) {
    log.warn('font catalog fetch failed; picker will show an empty list', e)
    return []
  }
  return catalog.map(
    (rec): FontCatalogEntry => ({
      value: rec.slug,
      label: rec.slug,
      family: rec.slug,
      weights: [400],
    }),
  )
}
